import { SocketEvents } from '@thinkalpha/ui-types/contracts/push';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { eventChannel } from 'redux-saga';
import type { EventChannel, Task } from 'redux-saga';
import { all, call, cancel, cancelled, fork, put, take, select } from 'redux-saga/effects';
import { api } from 'src/api';
import { sessionBarrierSocket } from 'src/lib/push';
import type { PushServiceSocket } from 'src/lib/push';
import { localforage } from 'src/lib/serialization/localForage';
import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import { becomeActiveApp, loseActive } from 'src/store/actions/app';
import { getAccessTokens, getRootUser } from 'src/store/selectors/auth';
import { v4 as uuid } from 'uuid';

const APP_ITEM_KEY = 'app-id';
const log = container.get('Logger').getSubLogger({ name: 'session' });

async function retrieveAppId() {
    return (await localforage.getItem<string>(APP_ITEM_KEY)) ?? undefined;
}

type SocketMessage = {
    type: 'active-app::claim';
    userId: string;
    appId: string;
};

function createSocketChannel(socket: PushServiceSocket) {
    log.info({ message: 'Creating socket channel for session service' });

    // `eventChannel` takes a subscriber function
    // the subscriber function takes an `emit` argument to put messages onto the channel
    return eventChannel((emit: (input: SocketMessage) => void) => {
        const handleActiveAppEvent = ({ appId, userId }: { appId: string; userId: string }) => {
            emit({ type: 'active-app::claim', appId, userId });
        };

        socket.on(SocketEvents.activeApp, handleActiveAppEvent);

        // the subscriber must return an unsubscribe function
        // this will be invoked when the saga calls `channel.close` method
        const unsubscribe = () => {
            socket.off(SocketEvents.activeApp, handleActiveAppEvent);
        };

        return unsubscribe;
    });
}

function* listenToPush(currentAppId: string) {
    const channel: EventChannel<Record<string, never>> = yield call(createSocketChannel, sessionBarrierSocket);

    try {
        while (true) {
            const socketAction: SocketMessage = yield take(channel);

            log.debug({ message: 'Received socket message', socketAction });

            if (socketAction.type === 'active-app::claim') {
                const newActiveAppId = socketAction.appId;

                if (newActiveAppId !== currentAppId) {
                    log.info({ message: 'Lost active app to claim from app id', newActiveAppId });

                    yield put(loseActive());
                } else {
                    log.info({ message: 'Server says we are the active app' });
                    yield put(becomeActiveApp());
                }
            }
        }
    } catch (e) {
        if (e instanceof Error) {
            log.error({ message: 'Error from push service socket', error: e });
        } else {
            log.fatal({ message: 'Unknown exception', details: e });
        }
    } finally {
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            log.info({ message: 'Closing channel for session barrier' });

            channel.close();
        }
    }
}

export function* claimAppId(appId: string) {
    // Don't have to wait for result, the socket will pick this up when verified
    const tokens: ReturnType<typeof getAccessTokens> = yield select(getAccessTokens);

    if (tokens.masqAccessToken) {
        log.debug({ message: 'Skipping app id claim while masquerading' });
        return;
    }

    // Use the access token, not the masq access token
    const token = tokens.accessToken?.token;

    if (!token) {
        log.warn({ message: 'No access token, cannot claim app id' });
        return;
    }

    const currentUser: UserDetails | undefined = yield select(getRootUser);

    yield call(() =>
        api.post('session/claims', { appId }, { headers: currentUser ? { Authorization: `Bearer ${token}` } : {} }),
    );
}

function* watchForClaimApp(appId: string) {
    try {
        while (true) {
            yield take('active-app::request');
            yield call(claimAppId, appId);
        }
    } catch (e) {
        log.error({ message: 'Error', error: e as Error });
    } finally {
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            log.info({ message: 'No longer listening for user to claim app' });
        }
    }
}

function* watchForSocketAuth(appId: string) {
    const authChannel = eventChannel((emit) => {
        const handleAuthSuccess = () => {
            log.info({ message: 'Socket authenticated, re-claiming app ID' });
            emit({ type: 'auth-success' });
        };

        sessionBarrierSocket.on('auth.success', handleAuthSuccess);

        // Cleanup function
        return () => {
            sessionBarrierSocket.off('auth.success', handleAuthSuccess);
        };
    });

    try {
        while (true) {
            yield take(authChannel);
            // If barrier is open, don't claim app ID on reconnect
            const isActive: RootState['app']['active'] = yield select((state) => state.app.active);
            if (isActive) {
                // Re-claim app ID whenever socket authenticates (initial auth or reconnect)
                yield call(claimAppId, appId);
            }
        }
    } catch (e) {
        log.error({ message: 'Error in socket auth watcher', error: e as Error });
    } finally {
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            authChannel.close();
        }
    }
}

function* setupService() {
    while (true) {
        // We only care about our session barrier if we are authenticated
        let accessToken = (yield select((state) => state.auth.accessToken)) as RootState['auth']['accessToken'];
        while (!accessToken) {
            yield take('access-token::set');
            accessToken = (yield select((state) => state.auth.accessToken)) as RootState['auth']['accessToken'];
        }

        const storedAppId: string | undefined = yield call(retrieveAppId);
        const appId = storedAppId ?? uuid();
        if (!storedAppId) {
            yield localforage.setItem(APP_ITEM_KEY, appId);
        }

        log.debug({ message: 'App id token for active app identification', appId });

        // Listen for the active-app being set by the server, claim the active app id for our own, and listen for
        // becoming the active app id

        // We do want to be listening first, so that when we claim, it's for sure
        const pushListener: Task = yield fork(listenToPush, appId);
        const appClaimListener: Task = yield fork(watchForClaimApp, appId);
        const socketAuthListener: Task = yield fork(watchForSocketAuth, appId);
        // Initial claim
        yield call(claimAppId, appId);

        // Wait for user to change, and then repeat this cycle
        const currentUser: ReturnType<typeof getRootUser> = yield select(getRootUser);
        listenForNewUser: while (true) {
            yield take('user-details::set');
            const newCurrentUser: ReturnType<typeof getRootUser> = yield select(getRootUser);
            if (newCurrentUser?.id !== currentUser?.id) {
                log.debug({ message: 'User changed, re-claiming app id' });
                break listenForNewUser;
            }
        }

        yield all([cancel(pushListener), cancel(appClaimListener), cancel(socketAuthListener)]);
    }
}

export function* sessionSagas() {
    yield all([setupService()]);
}
